import axios from 'axios';
import type { LoginCredentials, RegisterData, User, TrainingSlot, Booking, DefaultAssignment } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials) => {
    const response = await api.post<{ token: string; user: User }>('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  register: async (data: RegisterData) => {
    const response = await api.post<{ token: string; user: User }>('/auth/register', data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  getMe: async () => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },
};

// Slots API
export const slotsAPI = {
  getSlots: async (week?: string, all?: boolean) => {
    const params: any = {};
    if (week) {
      params.week = week;
    }
    if (all) {
      params.all = 'true';
    }
    const response = await api.get<TrainingSlot[]>('/slots', { params });
    return response.data;
  },

  getSlot: async (id: string) => {
    const response = await api.get<TrainingSlot>(`/slots/${id}`);
    return response.data;
  },

  createSlot: async (slot: Omit<TrainingSlot, 'id' | 'createdAt' | 'updatedAt' | 'bookingCount' | 'isFull' | 'availableSpots' | 'attendees'>) => {
    const response = await api.post<TrainingSlot | TrainingSlot[]>('/slots', slot);
    return response.data;
  },

  updateSlot: async (id: string, slot: Partial<TrainingSlot>) => {
    const response = await api.put<TrainingSlot>(`/slots/${id}`, slot);
    return response.data;
  },

  deleteSlot: async (id: string) => {
    await api.delete(`/slots/${id}`);
  },
};

// Bookings API
export const bookingsAPI = {
  getMyBookings: async () => {
    const response = await api.get<Booking[]>('/bookings/my-bookings');
    return response.data;
  },

  getSlotBookings: async (slotId: string) => {
    const response = await api.get<Booking[]>(`/bookings/slot/${slotId}`);
    return response.data;
  },

  createBooking: async (trainingSlotId: string, bookedDate: string) => {
    const response = await api.post<Booking>('/bookings', { trainingSlotId, bookedDate });
    return response.data;
  },

  updateBooking: async (id: string, userId: string) => {
    const response = await api.put<Booking>(`/bookings/${id}`, { userId });
    return response.data;
  },

  deleteBooking: async (id: string) => {
    await api.delete(`/bookings/${id}`);
  },
};

// Users API
export const usersAPI = {
  getUsers: async () => {
    const response = await api.get<User[]>('/users');
    return response.data;
  },

  createUser: async (data: { username: string; password: string; name?: string; surname?: string; role?: 'USER' | 'ADMIN' }) => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },

  bulkCreateUsers: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{ created: number; failed: number; users: User[] }>(
      '/users/bulk',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  updateUser: async (id: string, data: { username?: string; password?: string; name?: string; surname?: string; role?: 'USER' | 'ADMIN' }) => {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: string) => {
    const response = await api.delete<{ message: string }>(`/users/${id}`);
    return response.data;
  },

  downloadTemplate: async () => {
    const response = await api.get('/users/template', {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Default Assignments API
export const defaultAssignmentsAPI = {
  getDefaultAssignments: async (slotId?: string, userId?: string) => {
    const params: any = {};
    if (slotId) params.slotId = slotId;
    if (userId) params.userId = userId;
    const response = await api.get<DefaultAssignment[]>('/default-assignments', { params });
    return response.data;
  },

  getSlotDefaultAssignments: async (slotId: string) => {
    const response = await api.get<DefaultAssignment[]>(`/default-assignments/slot/${slotId}`);
    return response.data;
  },

  getUserDefaultAssignments: async (userId: string) => {
    const response = await api.get<DefaultAssignment[]>(`/default-assignments/user/${userId}`);
    return response.data;
  },

  createDefaultAssignment: async (userId: string, trainingSlotId: string) => {
    const response = await api.post<DefaultAssignment>('/default-assignments', {
      userId,
      trainingSlotId,
    });
    return response.data;
  },

  deleteDefaultAssignment: async (id: string) => {
    await api.delete(`/default-assignments/${id}`);
  },

  deleteDefaultAssignmentByUserAndSlot: async (userId: string, trainingSlotId: string) => {
    await api.delete('/default-assignments', {
      data: { userId, trainingSlotId },
    });
  },
};

export default api;

